import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import NotificationBell from './NotificationBell';
import ThemeToggle from './ThemeToggle';
import { getCurrentUser } from '../data/session';
import { logout } from '../services/auth-service';

const SettingsIcon = () => (
  <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
    <path d="M12,15.5A3.5,3.5 0 0,1 8.5,12A3.5,3.5 0 0,1 12,8.5A3.5,3.5 0 0,1 15.5,12A3.5,3.5 0 0,1 12,15.5M19.43,12.97C19.47,12.65 19.5,12.33 19.5,12C19.5,11.67 19.47,11.34 19.43,11L21.54,9.37C21.73,9.22 21.78,8.95 21.66,8.73L19.66,5.27C19.54,5.05 19.27,4.96 19.05,5.05L16.56,6.05C16.04,5.66 15.5,5.32 14.87,5.07L14.5,2.42C14.46,2.18 14.25,2 14,2H10C9.75,2 9.54,2.18 9.5,2.42L9.13,5.07C8.5,5.32 7.96,5.66 7.44,6.05L4.95,5.05C4.73,4.96 4.46,5.05 4.34,5.27L2.34,8.73C2.22,8.95 2.27,9.22 2.46,9.37L4.57,11C4.53,11.34 4.5,11.67 4.5,12C4.5,12.33 4.53,12.65 4.57,12.97L2.46,14.63C2.27,14.78 2.22,15.05 2.34,15.27L4.34,18.73C4.46,18.95 4.73,19.03 4.95,18.95L7.44,17.94C7.96,18.34 8.5,18.68 9.13,18.93L9.5,21.58C9.54,21.82 9.75,22 10,22H14C14.25,22 14.46,21.82 14.5,21.58L14.87,18.93C15.5,18.68 16.04,18.34 16.56,17.94L19.05,18.95C19.27,19.03 19.54,18.95 19.66,18.73L21.66,15.27C21.78,15.05 21.73,14.78 21.54,14.63L19.43,12.97Z"/>
  </svg>
);

const LogoutIcon = () => (
  <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
    <path d="M16,17V14H9V10H16V7L21,12L16,17M14,2A2,2 0 0,1 16,4V6H14V4H5V20H14V18H16V20A2,2 0 0,1 14,22H5A2,2 0 0,1 3,20V4A2,2 0 0,1 5,2H14Z"/>
  </svg>
);

const roleBadgeColor: Record<string, string> = {
  founder:  'bg-role-founder/15 text-role-founder border-role-founder/20',
  mentor:   'bg-role-mentor/15 text-role-mentor border-role-mentor/20',
  admin:    'bg-role-admin/15 text-role-admin border-role-admin/20',
  investor: 'bg-role-investor/15 text-role-investor border-role-investor/20',
};

const Topbar = () => {
  const [showProfile, setShowProfile] = useState(false);
  const profileRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const user = getCurrentUser();

  const initials = user.name
    ? user.name.split(' ').map((w: string) => w[0]).join('').toUpperCase().slice(0, 2)
    : '?';

  const [loggingOut, setLoggingOut] = useState(false);
  const handleLogout = async () => {
    if (loggingOut) return;
    setLoggingOut(true);
    try { await logout(); } finally { navigate('/login'); }
  };

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) {
        setShowProfile(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <header className="h-14 flex justify-end items-center shrink-0 mb-4">
      <div className="flex items-center gap-4 pr-8">
        <NotificationBell />
        <ThemeToggle />

        {/* Profile avatar button */}
        <div className="relative" ref={profileRef}>
          <button
            onClick={() => setShowProfile(v => !v)}
            title="Account"
            className="w-10 h-10 rounded-full overflow-hidden bg-chart/20 text-chart font-semibold text-sm flex items-center justify-center hover:bg-chart/30 transition-colors focus:outline-none focus:ring-2 focus:ring-chart/30"
          >
            {user.avatar ? (
              <img src={user.avatar} alt={user.name || 'avatar'} className="w-full h-full object-cover" />
            ) : (
              initials
            )}
          </button>

          {showProfile && (
            <div className="fixed mt-2 w-60 bg-shell border border-line rounded-card shadow-xl z-50 overflow-hidden" style={{ top: '3.5rem', right: '2rem' }}>
              {/* User info header */}
              <div className="px-4 py-3 bg-surface border-b border-line">
                <p className="text-sm font-semibold text-text">{user.name}</p>
                <p className="text-xs text-muted mt-0.5">{user.email || ''}</p>
                <span className={`inline-block mt-2 text-badge font-medium px-2.5 py-0.5 rounded-full border whitespace-nowrap capitalize ${roleBadgeColor[user.role] || 'bg-surface text-muted border-line'}`}>
                  {user.role}
                </span>
              </div>

              {/* Actions */}
              <div className="py-1">
                <Link
                  to="/app/settings"
                  onClick={() => setShowProfile(false)}
                  className="flex items-center gap-3 px-4 py-2.5 text-sm text-text hover:bg-surface transition-colors"
                >
                  <SettingsIcon />
                  <span>Settings</span>
                </Link>

                <button
                  onClick={handleLogout}
                  disabled={loggingOut}
                  className="flex items-center gap-3 px-4 py-2.5 text-sm text-danger hover:bg-danger/5 transition-colors w-full text-left disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <LogoutIcon />
                  <span>{loggingOut ? 'Logging out…' : 'Logout'}</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Topbar;
